import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { usePlatformRuntime } from '../runtime';
import type { RuntimeUser } from '../runtime';
import { apiClient } from '../api/client';
import { createTokenLifecycle } from '../runtime/tokenLifecycle';

type User = RuntimeUser;

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const runtime = usePlatformRuntime();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const lifecycleRef = useRef(
    createTokenLifecycle({
      session: runtime.session,
      apiClient,
      onSessionExpired: () => {
        runtime.session.clearAuthToken();
        runtime.session.clearRefreshToken();
        setUser(null);
        runtime.auth.redirectToSignedOut();
      },
    })
  );

  const startLifecycle = useCallback(() => {
    const token = runtime.session.getAuthToken();
    const refreshToken = runtime.session.getRefreshToken();
    if (token && refreshToken) {
      lifecycleRef.current.start(token, refreshToken);
    }
  }, [runtime]);

  const fetchUser = useCallback(async (): Promise<User | null> => {
    try {
      const nextUser = await runtime.auth.getCurrentUser();
      setUser(nextUser);
      startLifecycle();
      return nextUser;
    } catch (error) {
      if (runtime.session.getAuthToken()) {
        console.warn('[AuthContext] Failed to refresh current user', error);
      }
      if (!runtime.session.getAuthToken()) {
        setUser(null);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [runtime, startLifecycle]);

  useEffect(() => {
    void fetchUser();
    const lifecycle = lifecycleRef.current;
    return () => lifecycle.stop();
  }, [fetchUser]);

  useEffect(
    () => runtime.auth.subscribeToOAuthHandoff(() => void fetchUser()),
    [runtime, fetchUser]
  );

  const logout = async () => {
    lifecycleRef.current.stop();
    try {
      await runtime.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      runtime.session.clearAuthToken();
      runtime.session.clearRefreshToken();
      setUser(null);
      runtime.auth.redirectToSignedOut();
    }
  };

  const refreshUser = async (): Promise<User | null> => {
    return fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
